import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { ConnectionStatus } from '@erikdakoda/database/enums';

type ConnectionStatusChipProps = {
  readonly status: ConnectionStatus;
  readonly tagProps?: ChipProps;
};

const statusColors: Record<ConnectionStatus, ChipProps['color']> = {
  INITIALIZING: 'default',
  INITIATED: 'info',
  ACTIVE: 'success',
  FAILED: 'error',
  EXPIRED: 'warning',
  INACTIVE: 'default',
  REVOKED: 'error',
};

const statusI18nKeys: Record<ConnectionStatus, string> = {
  INITIALIZING: 'connectionStatusInitializing',
  INITIATED: 'connectionStatusInitiated',
  ACTIVE: 'connectionStatusActive',
  FAILED: 'connectionStatusFailed',
  EXPIRED: 'connectionStatusExpired',
  INACTIVE: 'connectionStatusInactive',
  REVOKED: 'connectionStatusRevoked',
};

/**
 * Displays a Composio connection lifecycle status as a compact chip.
 * @param props Chip configuration.
 * @param props.status Lifecycle status to display.
 * @param props.tagProps Optional Chip overrides.
 * @returns Styled connection-status chip.
 */
export default function ConnectionStatusChip({ status, tagProps }: ConnectionStatusChipProps) {
  'use memo';

  const { t } = useTranslation('connection');

  return (
    <Chip
      label={t(statusI18nKeys[status])}
      variant="outlined"
      size="small"
      color={statusColors[status]}
      {...tagProps}
      className={clsx('connection-status-chip', tagProps?.className)}
    />
  );
}
